/**
 * manpower/useManpower/useManpowerEdits.js - the calendar's editing API:
 * custom projects, colours, writing / moving days and project numbers, and
 * what is on a person's day.
 * Moved verbatim out of useManpower.js, which calls it at the same point
 * (same hook order) with the state and refs it reads.
 */
import { useCallback } from 'react';
import { defaultProjectColor, ymd } from '../manpowerHelpers';
import { moveDays, moveDetails, cleanDetail } from './scheduleChanges';

export function useManpowerEdits({ setCustomProjects, setEdits, setPalette, palette, setSchedule, edits, details }) {
  const addCustomProject = useCallback(
    (name) => {
      setCustomProjects((cur) => (cur.includes(name) ? cur : [...cur, name]));
    },
    [setCustomProjects]
  );

  // Removing a project also clears every assignment that uses it — otherwise
  // those cells would still show the label but the picker would no longer
  // offer it, which is confusing.
  const deleteCustomProject = useCallback(
    (name) => {
      setCustomProjects((cur) => cur.filter((p) => p !== name));
      setEdits((cur) => {
        const next = { ...cur };
        for (const k of Object.keys(next)) if (next[k] === name) delete next[k];
        return next;
      });
      setPalette((cur) => {
        if (!cur[name]) return cur;
        const next = { ...cur };
        delete next[name];
        return next;
      });
    },
    [setCustomProjects, setEdits, setPalette]
  );

  const setPaletteOverride = useCallback(
    (code, hex) => {
      setPalette((cur) => {
        const next = { ...cur };
        if (!hex) delete next[code];
        else next[code] = hex;
        return next;
      });
    },
    [setPalette]
  );

  const colorFor = useCallback((code) => palette[code] || defaultProjectColor(code), [palette]);

  // moveDays + moveDetails as one step, on one snapshot.
  const writeDays = useCallback(
    (engineerName, old, dateFrom, dateTo, label, detail) => {
      setSchedule((cur) => {
        const nextEdits = moveDays(cur.edits, engineerName, old, dateFrom, dateTo, label);
        return {
          edits: nextEdits,
          details: moveDetails(cur.details, nextEdits, engineerName, old, dateFrom, dateTo, label, detail, cur.edits),
        };
      });
    },
    [setSchedule]
  );

  /**
   * Write one engineer's schedule across a date range. `project` '' or null
   * clears the days. Every caller goes through here — the calendar cell
   * editor and the engineer's own card both — so neither can drift.
   * `detail` = { location, projectNumber, status } for the days written;
   * leave it out and a day keeps its details only if it keeps its label.
   */
  const applyEdit = useCallback(
    (engineerName, dateFrom, dateTo, project, detail) => {
      const label = project || null;
      writeDays(engineerName, null, dateFrom, dateTo, label, detail);
      if (label) addCustomProject(label);
    },
    [writeDays, addCustomProject]
  );

  /**
   * Edit an EXISTING block: take it off the days it was on, then put the result
   * on the dates chosen. Saving used to only ever ADD days, so moving someone
   * to new dates copied the block, and shortening or starting it later changed
   * nothing - while the save still "succeeded". `old` = { from, to, code } of
   * the block the editor was opened on.
   */
  const moveEdit = useCallback(
    (engineerName, old, dateFrom, dateTo, project, detail) => {
      const label = project || null;
      writeDays(engineerName, old, dateFrom, dateTo, label, detail);
      if (label) addCustomProject(label);
    },
    [writeDays, addCustomProject]
  );

  /**
   * Put one Project # on many days at once (the editor's "also fill" boxes):
   * each key still carrying `label` gets it, keeping its Location / Status.
   * A day relabelled or cleared since the keys were worked out is skipped.
   */
  const fillProjectNumber = useCallback(
    (keys, label, projectNumber) => {
      const num = String(projectNumber || '').trim();
      if (!num || !keys || !keys.length) return;
      setSchedule((cur) => {
        let details = null;
        for (const k of keys) {
          if (!label || cur.edits[k] !== label) continue;
          const was = cur.details[k] || {};
          if (was.status === 'PTO' || was.projectNumber === num) continue;
          if (!details) details = { ...cur.details };
          details[k] = cleanDetail({ ...was, projectNumber: num });
        }
        return details ? { edits: cur.edits, details } : cur;
      });
    },
    [setSchedule]
  );

  /** What this engineer is on today, or '' — the card's Current Project. */
  const projectOn = useCallback((engineerName, date = new Date()) => edits[`${engineerName}|${ymd(date)}`] || '', [edits]);

  /** That day's { location?, projectNumber?, status? }, or null. */
  const detailOn = useCallback((engineerName, date = new Date()) => details[`${engineerName}|${ymd(date)}`] || null, [details]);
  return {
    setPaletteOverride,
    colorFor,
    addCustomProject,
    deleteCustomProject,
    applyEdit,
    moveEdit,
    fillProjectNumber,
    projectOn,
    detailOn,
  };
}
